import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { Copy, KeyRound, Webhook, Code as CodeIcon, Plug, ShieldCheck, CheckCircle2, AlertCircle, Eye, EyeOff, Smartphone } from "lucide-react";
import { toast } from "sonner";

export default function ApiDocs() {
  const [meta, setMeta] = useState(null);
  const [sms, setSms] = useState(null);

  useEffect(() => {
    api.get("/erp-meta").then(r => setMeta(r.data)).catch(() => {});
    api.get("/admin/sms/credits").then(r => setSms(r.data)).catch(() => setSms({ ok: false, error: "No se pudo consultar saldo" }));
  }, []);

  const copy = async (t) => {
    await navigator.clipboard.writeText(t);
    toast.success("Copiado");
  };

  const apiBase = meta?.api_base || (typeof window !== "undefined" ? `${window.location.origin}/api` : "/api");
  const apiKey = meta?.api_key_preview || "vp_•••••";
  const apiKeyFull = meta?.api_key_full || "";
  const whSecret = meta?.webhook_secret_preview || "whs_•••••";
  const whSecretFull = meta?.webhook_secret_full || "";

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="apidocs-root">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500">Integración</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900">API REST para Ekspertium</h1>
          <p className="text-slate-500 mt-1">Conecta tu ERP con la plataforma de videoperitación. Autenticación por <code className="text-xs px-1.5 py-0.5 bg-slate-100 rounded">X-API-Key</code>, webhooks firmados con HMAC-SHA256.</p>
        </div>
      </div>

      {/* Status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatusCard label="API entrante" ok value="Activa" desc="POST /api/erp/sessions, fotos, certificado" icon={Plug} />
        <StatusCard label="Webhooks salientes" ok={!!meta?.webhooks_enabled} value={meta?.webhooks_enabled ? "Activos" : "No configurados"} desc={meta?.webhook_url || "Configura WEBHOOK_URL o EKSPERTIUM_BASE_URL"} icon={Webhook} />
        <StatusCard label="ERP saliente (callbacks)" ok={!!meta?.erp_outbound_enabled} value={meta?.erp_outbound_enabled ? "Activo" : "No configurado"} desc={meta?.ekspertium_base_url || "Pendiente EKSPERTIUM_BASE_URL"} icon={ShieldCheck} />
      </div>

      {/* SMS provider */}
      <SmsCreditsPanel data={sms} onRefresh={() => { setSms(null); api.get("/admin/sms/credits").then(r => setSms(r.data)).catch(() => setSms({ ok: false, error: "No se pudo consultar saldo" })); }} />

      {/* Credentials */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="h-4 w-4 text-blue-600" />
          <h2 className="font-heading text-xl font-semibold">Credenciales</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <CredField label="Base URL" value={apiBase} onCopy={() => copy(apiBase)} />
          <CredField label="API Key (X-API-Key)" preview={apiKey} fullValue={apiKeyFull} onCopy={() => copy(apiKeyFull)} hint="Úsala en la cabecera X-API-Key de tu ERP. Pulsa el ojo para verla completa." mono revealable />
          <CredField label="Webhook Secret" preview={whSecret} fullValue={whSecretFull} onCopy={() => copy(whSecretFull)} hint="Para verificar X-VP-Signature en tu endpoint. Pulsa el ojo para verlo." mono revealable />
          <CredField label="Cabecera de firma" value="X-VP-Signature: sha256=<HMAC-SHA256(secret, raw_body)>" mono />
        </div>
      </div>

      <Tabs defaultValue="quickstart">
        <TabsList>
          <TabsTrigger value="quickstart">Inicio rápido</TabsTrigger>
          <TabsTrigger value="endpoints">Endpoints</TabsTrigger>
          <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
          <TabsTrigger value="errors">Errores</TabsTrigger>
        </TabsList>

        {/* Quickstart */}
        <TabsContent value="quickstart">
          <div className="space-y-4">
            <Card title="1. Crear una sesión de videoperitación desde Ekspertium">
              <CodeBlock text={`curl -X POST "${apiBase}/erp/sessions" \\
  -H "X-API-Key: $VP_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "expediente_num": "EXP-2026-001",
    "asegurado_nombre": "María García",
    "asegurado_telefono": "+34612345678",
    "descripcion": "Daño en parachoques delantero",
    "canal": "sms",
    "send_invite": true
  }'`} />
              <p className="text-xs text-slate-500 mt-2">Devuelve <code>invite_url</code>. Si <code>send_invite=false</code>, lo distribuye Ekspertium por su propio canal.</p>
            </Card>

            <Card title="2. Consultar estado en cualquier momento">
              <CodeBlock text={`curl "${apiBase}/erp/sessions/by-expediente/EXP-2026-001" \\
  -H "X-API-Key: $VP_API_KEY"`} />
            </Card>

            <Card title="3. Descargar el certificado PDF al finalizar">
              <CodeBlock text={`curl "${apiBase}/erp/sessions/{siniestro_id}/certificate" \\
  -H "X-API-Key: $VP_API_KEY" \\
  -o certificado.pdf`} />
            </Card>

            <Card title="4. Listar fotos individualmente (cada una con su geolocalización)">
              <CodeBlock text={`curl "${apiBase}/erp/sessions/{siniestro_id}/photos" \\
  -H "X-API-Key: $VP_API_KEY"

# Respuesta:
{
  "items": [{
    "id": "uuid", "siniestro_id": "uuid", "url": "${apiBase}/erp/photos/{photo_id}",
    "lat": 40.4168, "lng": -3.7038, "accuracy": 12,
    "captured_at": "2026-02-15T10:00:00+00:00",
    "filename": "capture_20260215_100000.jpg"
  }],
  "total": 5
}`} />
            </Card>

            <Card title="5. Embeber la sala del perito en un iframe (Avizor / ERP) — IMPORTANTE">
              <p className="text-sm text-slate-700 mb-2">
                Si tu ERP embebe el <code>expert_url</code> dentro de un <code>&lt;iframe&gt;</code>, el navegador
                <strong> bloquea por defecto el acceso a cámara y micrófono</strong> dentro de iframes cross-origin.
                Si no añades el atributo <code>allow=&quot;...&quot;</code> al iframe, el perito verá un error de
                permisos y la videoperitación quedará en estado <em>&quot;Reconectando…&quot;</em> indefinidamente.
              </p>
              <CodeBlock text={`<!-- Mínimo imprescindible para que funcione la sala del perito -->
<iframe
  src="{expert_url}"
  allow="camera; microphone; display-capture; autoplay; geolocation; clipboard-read; clipboard-write"
  style="width:100%; height:100%; border:0;"
></iframe>`} />
              <p className="text-xs text-slate-500 mt-2">
                Alternativa: abrir el <code>expert_url</code> con <code>window.open(url, &quot;_blank&quot;)</code>
                en una pestaña nueva (sin iframe) — los permisos funcionan sin ningún atributo extra.
              </p>
            </Card>
          </div>
        </TabsContent>

        {/* Endpoints */}
        <TabsContent value="endpoints">
          <div className="space-y-2">
            {ENDPOINTS.map((e) => (
              <div key={`${e.method}:${e.path}`} className="bg-white border border-slate-200 rounded-lg p-4 flex items-start gap-4">
                <Badge variant="outline" className={methodColor(e.method)}>{e.method}</Badge>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm text-slate-900 break-all">{e.path}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{e.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Webhooks */}
        <TabsContent value="webhooks">
          <div className="space-y-4">
            <WebhookStatusPanel apiBase={apiBase} />

            <Card title="Eventos enviados a tu URL de webhook">
              <p className="text-sm text-slate-600 mb-2">Configura <code>WEBHOOK_URL</code> o <code>EKSPERTIUM_BASE_URL</code> en el <code>.env</code> de la app. Recibirás un POST JSON por cada evento con cabeceras:</p>
              <CodeBlock text={`POST {WEBHOOK_URL}
Content-Type: application/json
X-API-Key: $EKSPERTIUM_API_KEY
X-VP-Event: session.photo_captured
X-VP-Timestamp: 1778856123
X-VP-Signature: sha256=4f3e2a...

{
  "event": "session.photo_captured",
  "ts": 1778856123,
  "data": {
    "siniestro_id": "uuid",
    "expediente_num": "EXP-2026-001",
    "folder": "Eksvidi",
    "photo": {
      "id": "uuid",
      "filename": "foto_xxxxxxxx.jpg",
      "folder": "Eksvidi",
      "category": "photo",
      "content_type": "image/jpeg",
      "public_url": "${apiBase}/photos/{photo_id}",
      "download_url": "${apiBase}/erp/photos/{photo_id}",
      "lat": 40.4168, "lng": -3.7038, "accuracy": 12,
      "captured_at": "2026-02-15T10:00:00+00:00"
    }
  }
}`} />
            </Card>

            <Card title="Lista de eventos">
              <div className="text-sm space-y-1.5">
                {WEBHOOKS.map(([n, d]) => (
                  <div key={n} className="flex gap-3"><code className="text-xs px-1.5 py-0.5 bg-slate-100 rounded shrink-0 font-semibold">{n}</code><span className="text-slate-600">{d}</span></div>
                ))}
              </div>
            </Card>

            <Card title="Verificación de firma (Python, FastAPI)">
              <CodeBlock text={`import hmac, hashlib

SECRET = os.environ["WEBHOOK_SECRET"]

@app.post("/api/external/videoperitaje/webhooks")
async def vp_webhook(request: Request):
    body = await request.body()
    sig = request.headers.get("X-VP-Signature", "")
    expected = "sha256=" + hmac.new(SECRET.encode(), body, hashlib.sha256).hexdigest()
    if not hmac.compare_digest(sig, expected):
        raise HTTPException(403, "Firma inválida")
    event = json.loads(body)
    # procesa event["event"] y event["data"] ...
    return {"ok": True}`} />
            </Card>
          </div>
        </TabsContent>

        {/* Errors */}
        <TabsContent value="errors">
          <Card title="Códigos de error">
            <div className="text-sm space-y-2">
              <Row code="401 Unauthorized" desc='Falta cabecera X-API-Key (o Authorization: Bearer).' />
              <Row code="403 Forbidden" desc="API Key incorrecta." />
              <Row code="404 Not Found" desc="Siniestro / expediente / foto no existe." />
              <Row code="400 Bad Request" desc="Datos del body inválidos. Mensaje en `detail`." />
              <Row code="500 Internal" desc="Error interno. Reintenta con backoff exponencial." />
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const ENDPOINTS = [
  { method: "POST", path: "/api/erp/sessions", desc: "Crear una nueva sesión de videoperitación desde el ERP." },
  { method: "GET",  path: "/api/erp/sessions?status_filter=&expediente_num=&limit=&offset=", desc: "Listar/paginar sesiones." },
  { method: "GET",  path: "/api/erp/sessions/{id}", desc: "Detalle completo (siniestro + fotos + transcripción + chat)." },
  { method: "GET",  path: "/api/erp/sessions/by-expediente/{expediente_num}", desc: "Buscar por nº de expediente." },
  { method: "GET",  path: "/api/erp/sessions/{id}/photos", desc: "Listar fotos como recursos individuales con metadatos." },
  { method: "GET",  path: "/api/erp/photos/{photo_id}", desc: "Descargar imagen binaria (image/jpeg)." },
  { method: "GET",  path: "/api/erp/sessions/{id}/transcript", desc: "Transcripción (segmentos + texto completo)." },
  { method: "GET",  path: "/api/erp/sessions/{id}/certificate", desc: "Descargar PDF del certificado final." },
  { method: "POST", path: "/api/erp/sessions/{id}/resend-invite", desc: "Reenviar SMS/WhatsApp al asegurado." },
  { method: "POST", path: "/api/erp/sessions/{id}/cancel", desc: "Cancelar la sesión." },
  { method: "POST", path: "/api/erp/sessions/{id}/finalize", desc: "Forzar finalización + generar certificado." },
  { method: "GET",  path: "/api/erp/peritos", desc: "Listar peritos disponibles." },
  { method: "GET",  path: "/api/erp/health", desc: "Health-check y flags de configuración." },
];

const WEBHOOKS = [
  ["session.created", "Sesión recién creada (incluye invite_url)."],
  ["session.consent_accepted", "Asegurado ha aceptado el consentimiento RGPD."],
  ["session.photo_captured", "Foto capturada con geolocalización y URL."],
  ["session.finalized", "Sesión finalizada con certificate_url + fotos + transcripción."],
  ["session.cancelled", "Sesión cancelada."],
];

function methodColor(m) {
  if (m === "GET") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (m === "POST") return "bg-blue-50 text-blue-700 border-blue-200";
  if (m === "DELETE") return "bg-red-50 text-red-700 border-red-200";
  return "bg-slate-50 text-slate-700 border-slate-200";
}

function Card({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
      <h3 className="font-heading font-semibold mb-3">{title}</h3>
      {children}
    </div>
  );
}

function CodeBlock({ text }) {
  return (
    <pre className="bg-[#0F172A] text-blue-100 text-xs rounded-lg p-4 overflow-x-auto font-mono leading-relaxed">
      <code>{text}</code>
    </pre>
  );
}

function CredField({ label, value, preview, fullValue, onCopy, hint, mono, revealable }) {
  const [shown, setShown] = useState(false);
  const display = revealable ? (shown && fullValue ? fullValue : preview) : value;
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/40">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <div className={`flex-1 text-sm break-all ${mono ? "font-mono" : ""} text-slate-900`}>{display}</div>
        {revealable && (
          <Button size="icon" variant="outline" onClick={() => setShown((s) => !s)} className="h-7 w-7" data-testid={`reveal-${label}`}>
            {shown ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </Button>
        )}
        {onCopy && (
          <Button size="icon" variant="outline" onClick={onCopy} className="h-7 w-7" data-testid={`copy-${label}`}>
            <Copy className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

function StatusCard({ label, ok, value, desc, icon: Icon }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</div>
        <div className={`h-8 w-8 rounded-lg border flex items-center justify-center ${ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="font-heading text-xl font-bold text-slate-900 mt-2 flex items-center gap-2">
        {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertCircle className="h-4 w-4 text-amber-600" />}
        {value}
      </div>
      <div className="text-[11px] text-slate-500 mt-1 truncate">{desc}</div>
    </div>
  );
}

function WebhookStatusPanel({ apiBase }) {
  const [cfg, setCfg] = useState(null);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [eventName, setEventName] = useState("session.test");
  const [pinging, setPinging] = useState(false);
  const [pingResult, setPingResult] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagResult, setDiagResult] = useState(null);
  const [diagOpen, setDiagOpen] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/admin/webhooks/config");
      setCfg(r.data);
    } catch (e) {
      setCfg({ enabled: false, error: e?.response?.data?.detail || "Error" });
    }
  };
  useEffect(() => { refresh(); }, []);

  const sendTest = async () => {
    setSending(true);
    setLastResult(null);
    try {
      const r = await api.post("/admin/webhooks/test", { event: eventName });
      setLastResult(r.data);
      if (r.data?.ok) {
        // 200 OK del transporte NO implica que Ekspertium haya procesado. Detectamos
        // respuestas típicas de "recibido pero descartado" para avisar al perito.
        const body = (r.data?.response_body || "").toLowerCase();
        if (body.includes("skipped") || body.includes("not_found") || body.includes("not found")) {
          toast.warning(`Ekspertium contestó 200 pero descartó el evento: ${r.data.response_body?.slice(0,200)}`, { duration: 12000 });
        } else {
          toast.success(`Webhook entregado · HTTP ${r.data.status}`);
        }
      } else {
        toast.error(`Fallo: ${r.data?.error || "ver detalle"}`);
      }
    } catch (e) {
      setLastResult({ ok: false, error: e?.response?.data?.detail || String(e) });
      toast.error("Error enviando webhook");
    } finally {
      setSending(false);
    }
  };

  const doPing = async () => {
    setPinging(true);
    setPingResult(null);
    try {
      const r = await api.post("/admin/webhooks/ping");
      setPingResult(r.data);
      if (r.data?.reachable) toast.success(`URL alcanzable · HTTP ${r.data.status}`);
      else toast.error(`No alcanzable: ${r.data?.error || "sin respuesta"}`);
    } catch (e) {
      setPingResult({ ok: false, reachable: false, error: e?.response?.data?.detail || String(e) });
      toast.error("Ping fallido");
    } finally {
      setPinging(false);
    }
  };

  const doDiagnose = async () => {
    setDiagnosing(true);
    setDiagResult(null);
    try {
      const r = await api.post("/admin/webhooks/diagnose", { event: eventName });
      setDiagResult(r.data);
      setDiagOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al diagnosticar");
    } finally {
      setDiagnosing(false);
    }
  };

  const copy = async (t) => {
    try {
      await navigator.clipboard.writeText(t || "");
      toast.success("Copiado");
    } catch { /* ignore */ }
  };

  const enabled = !!cfg?.enabled;
  return (
    <div className="bg-white border border-[#E5E2D8] rounded-[14px] p-6" data-testid="webhook-status-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Webhook className="h-4 w-4 text-[#D85A30]" />
            <h3 className="font-heading text-lg font-semibold text-[#2C2C2A]">Estado de los webhooks salientes</h3>
            <Badge variant="outline" className={enabled ? "bg-[#E6F5EF] text-[#1D9E75] border-transparent rounded-full" : "bg-[#FAECE7] text-[#A14620] border-transparent rounded-full"}>
              {enabled ? "Activos" : "Desactivados"}
            </Badge>
          </div>
          <p className="text-xs text-[#7A786F]">Cuando Eksvidi dispare un evento, hará POST firmado a la URL de abajo.</p>
        </div>
        <Button onClick={refresh} variant="outline" size="sm" className="border-[#E5E2D8] rounded-[10px]" data-testid="refresh-webhook-cfg">Actualizar</Button>
      </div>

      <div className="grid md:grid-cols-2 gap-3 mt-4">
        <div className="border border-[#E5E2D8] rounded-[12px] p-3 bg-[#FBFAF6]">
          <div className="text-[11px] uppercase tracking-wider text-[#7A786F] font-semibold mb-1">URL destino</div>
          <div className="text-sm font-mono break-all text-[#2C2C2A]" data-testid="webhook-url-value">{cfg?.url || "—"}</div>
        </div>
        <div className="border border-[#E5E2D8] rounded-[12px] p-3 bg-[#FBFAF6]">
          <div className="text-[11px] uppercase tracking-wider text-[#7A786F] font-semibold mb-1">Cabecera de firma</div>
          <div className="text-sm font-mono text-[#2C2C2A]">{cfg?.signature_header || "X-VP-Signature"} · <span className="text-[#7A786F]">{cfg?.signature_format || "sha256=<hex>"}</span></div>
          <div className="text-[11px] text-[#7A786F] mt-1">Secret: {cfg?.secret_configured ? "configurado ✓" : "sin configurar"}</div>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <select
          value={eventName}
          onChange={(e) => setEventName(e.target.value)}
          className="text-sm border border-[#E5E2D8] rounded-[10px] px-3 h-9 bg-white text-[#2C2C2A]"
          data-testid="webhook-event-select"
        >
          {["session.test", "session.created", "session.consent_accepted", "session.photo_captured", "session.finalized", "session.cancelled"].map(ev => (
            <option key={ev} value={ev}>{ev}</option>
          ))}
        </select>
        <Button onClick={sendTest} disabled={!enabled || sending} className="btn-eks-primary rounded-[10px]" data-testid="send-test-webhook-btn">
          {sending ? "Enviando…" : "Probar envío real"}
        </Button>
        <Button onClick={doPing} disabled={!enabled || pinging} variant="outline" className="border-[#E5E2D8] rounded-[10px]" data-testid="ping-webhook-btn">
          {pinging ? "Comprobando…" : "Ping URL"}
        </Button>
        <Button onClick={doDiagnose} disabled={diagnosing} variant="outline" className="border-[#E5E2D8] rounded-[10px]" data-testid="diagnose-webhook-btn">
          {diagnosing ? "Analizando…" : "Ver payload exacto"}
        </Button>
        {lastResult && (
          <span className={`inline-flex items-center gap-1 text-xs ${lastResult.ok ? "text-[#1D9E75]" : "text-[#A14620]"}`} data-testid="webhook-result">
            {lastResult.ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
            {lastResult.ok ? `Entregado · HTTP ${lastResult.status}` : `Error: ${lastResult.error || "ver logs"}`}
          </span>
        )}
        {pingResult && (
          <span className={`inline-flex items-center gap-1 text-xs ${pingResult.reachable ? "text-[#1D9E75]" : "text-[#A14620]"}`} data-testid="ping-result">
            {pingResult.reachable ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
            {pingResult.reachable ? `URL alcanzable · HTTP ${pingResult.status}` : `No alcanzable: ${pingResult.error || "ver logs"}`}
          </span>
        )}
      </div>

      {/* Response body de Ekspertium (revela si el ERP procesó o descartó) */}
      {lastResult?.response_body && (
        <div className="mt-3 border border-[#E5E2D8] rounded-[10px] bg-white p-3" data-testid="webhook-response-body">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[11px] uppercase tracking-wider text-[#7A786F] font-semibold">Respuesta de Ekspertium</div>
            <span className="text-[11px] text-[#7A786F]">HTTP {lastResult.status}</span>
          </div>
          <pre className="text-[12px] font-mono text-[#2C2C2A] whitespace-pre-wrap break-all">{lastResult.response_body}</pre>
          {(() => {
            const b = (lastResult.response_body || "").toLowerCase();
            if (b.includes("expediente_not_found") || (b.includes("skipped") && b.includes("not_found"))) {
              return (
                <div className="mt-2 border-l-4 border-[#D85A30] bg-[#FDF1EC] rounded-[8px] p-2 text-[12px] text-[#2C2C2A]">
                  <strong>Diagnóstico:</strong> el webhook llega correctamente firmado, pero Ekspertium NO encuentra el expediente por el <code>expediente_num</code> enviado. Comprueba con Ekspertium (a) qué campo esperan como clave (¿<code>expediente_num</code>, <code>ref</code>, <code>expediente_id</code>?) y (b) si la BD apunta al mismo entorno (preview vs producción) que la app Eksvidi está usando.
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}

      {/* Warnings del diagnostic */}
      {diagResult?.warnings?.length > 0 && (
        <div className="mt-4 border-l-4 border-[#D85A30] bg-[#FDF1EC] rounded-[10px] p-3" data-testid="webhook-warnings">
          <div className="text-xs uppercase tracking-wider text-[#A14620] font-semibold mb-1 flex items-center gap-1">
            <AlertCircle className="h-3.5 w-3.5" /> Aviso de configuración
          </div>
          <ul className="list-disc pl-5 space-y-1 text-sm text-[#2C2C2A]">
            {diagResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      {/* Diagnostic panel */}
      {diagResult && diagOpen && (
        <div className="mt-4 border border-[#E5E2D8] rounded-[12px] bg-[#FBFAF6] p-4 space-y-3" data-testid="webhook-diagnostic-panel">
          <div className="flex items-center justify-between">
            <div className="font-heading text-sm font-semibold text-[#2C2C2A]">Payload exacto que envía Eksvidi</div>
            <button onClick={() => setDiagOpen(false)} className="text-xs text-[#7A786F] hover:text-[#2C2C2A]">Cerrar</button>
          </div>
          <p className="text-xs text-[#7A786F]">Copia estos 3 bloques y compártelos con el equipo de Ekspertium para que verifiquen que su endpoint procesa correctamente el mismo body + firma.</p>

          <DiagBlock label={`POST ${diagResult.url || "(sin URL)"}`} value={diagResult.url || ""} onCopy={() => copy(diagResult.url || "")} />

          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[11px] uppercase tracking-wider text-[#7A786F] font-semibold">Headers</div>
              <button onClick={() => copy(Object.entries(diagResult.headers || {}).map(([k,v]) => `${k}: ${v}`).join("\n"))} className="text-xs text-[#D85A30] hover:underline">Copiar</button>
            </div>
            <pre className="bg-white border border-[#E5E2D8] rounded-[10px] p-3 text-[11px] font-mono overflow-x-auto whitespace-pre-wrap break-all">{Object.entries(diagResult.headers || {}).map(([k,v]) => `${k}: ${v}`).join("\n")}</pre>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[11px] uppercase tracking-wider text-[#7A786F] font-semibold">Body (JSON raw){diagResult.body_raw_truncated ? " · truncado a 12kB" : ""}</div>
              <button onClick={() => copy(diagResult.body_raw || "")} className="text-xs text-[#D85A30] hover:underline">Copiar</button>
            </div>
            <pre className="bg-white border border-[#E5E2D8] rounded-[10px] p-3 text-[11px] font-mono overflow-x-auto max-h-80 whitespace-pre-wrap break-all">{diagResult.body_raw || ""}</pre>
          </div>

          <div className="text-[11px] text-[#7A786F]">Algoritmo: <code className="bg-white border border-[#E5E2D8] rounded px-1">{diagResult.signature_algorithm}</code></div>
        </div>
      )}
    </div>
  );
}

function DiagBlock({ label, value, onCopy }) {
  return (
    <div className="flex items-start gap-2">
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-[#7A786F] font-semibold mb-1">URL</div>
        <div className="text-sm font-mono break-all text-[#2C2C2A] bg-white border border-[#E5E2D8] rounded-[10px] px-3 py-2">{label}</div>
      </div>
      <button onClick={onCopy} className="mt-6 text-xs text-[#D85A30] hover:underline shrink-0" title={value}>Copiar</button>
    </div>
  );
}


function SmsCreditsPanel({ data, onRefresh }) {
  const loading = data === null;
  const ok = data?.ok;
  const credits = data?.credits;
  const low = data?.low;
  const threshold = data?.low_threshold ?? 50;
  const sender = data?.sender;

  let badgeColor = "bg-slate-100 text-slate-700";
  let icon = <Smartphone className="h-5 w-5 text-slate-500" />;
  const title = "Saldo SMS (Mensatek)";
  let value = "—";
  let desc = "Consultando…";

  if (!loading && ok && credits !== null && credits !== undefined) {
    value = `${credits} créditos`;
    desc = sender ? `Remitente: ${sender}` : "Mensatek API v7";
    if (low) {
      badgeColor = "bg-amber-50 text-amber-800 border border-amber-200";
      icon = <AlertCircle className="h-5 w-5 text-amber-600" />;
      desc = `Saldo bajo (< ${threshold}). Recarga en mensatek.com para no quedarte sin créditos.`;
    } else {
      badgeColor = "bg-emerald-50 text-emerald-800 border border-emerald-200";
      icon = <CheckCircle2 className="h-5 w-5 text-emerald-600" />;
    }
  } else if (!loading && !ok) {
    badgeColor = "bg-rose-50 text-rose-800 border border-rose-200";
    icon = <AlertCircle className="h-5 w-5 text-rose-600" />;
    value = "Desconectado";
    desc = data?.error ? `Error: ${data.error}` : "No se pudo consultar el saldo en Mensatek.";
  }

  return (
    <div className={`flex items-center gap-4 rounded-xl p-4 ${badgeColor}`} data-testid="sms-credits-panel">
      <div className="shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase tracking-wider opacity-70">{title}</div>
        <div className="font-heading text-2xl font-semibold leading-tight" data-testid="sms-credits-value">{value}</div>
        <div className="text-xs opacity-80 mt-0.5">{desc}</div>
      </div>
      <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading} data-testid="sms-credits-refresh">
        {loading ? "…" : "Actualizar"}
      </Button>
    </div>
  );
}




function Row({ code, desc }) {
  return (
    <div className="flex gap-3">
      <code className="text-xs px-1.5 py-0.5 bg-slate-100 rounded shrink-0 font-semibold">{code}</code>
      <span className="text-slate-600">{desc}</span>
    </div>
  );
}
